import React, { memo, useEffect, useMemo } from 'react';

import { orderBy } from 'lodash';
import { Controller, UseFormReturn } from 'react-hook-form';

import useUserHook from 'hooks/useUserHook';

import { IContract, IProject } from 'store/projectContract.store';

import { useQueryGetStoredTags } from 'api/queries/getStoredTags.query';

import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import classes from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage.module.scss';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { ICardActivityCode } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';
import { Select } from 'components/DesignSystem/Select';
import { OptionsWithExclude } from 'components/DesignSystem/Select/components/OptionsWithExclude/OptionsWithExclude';

interface ITagsFilterProps {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    value?: string[];
    options?: ICardActivityCode[];
    isSelected?: boolean;
    project?: IProject | null;
    contract?: IContract | null;
}

interface Value {
    name: string;
    isExclude: boolean;
}

export const TagsFilter = memo(
    ({
        methods,
        name,
        filtersStatusRef,
        handleFilters,
        value,
        isSelected = true,
        project,
        contract,
    }: ITagsFilterProps) => {
        const { customerId } = useUserHook();
        const { data: tags, isSuccess } = useQueryGetStoredTags({
            enabled: true,
            customerId,
            projectId: project?.id,
            contractId: contract?.id,
        });

        const options = useMemo(
            () =>
                tags
                    ? orderBy(
                          tags.map((item) => ({
                              label: item.name,
                              value: { name: item.name, isExclude: false },
                          })),
                          [(item: any) => item['label'].toLowerCase()],
                          ['asc'],
                      )
                    : [],
            [tags],
        );

        useEffect(() => {
            if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
                return;
            }
            signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
        }, [value, name, filtersStatusRef, isSuccess]);

        const handleChange = (value: readonly ISelectOption<Value>[]) => {
            methods?.setValue(
                name,
                value.map((item) => item.value),
            );
            handleFilters && handleFilters();
        };

        /**
         * 1) VALIDATING the selection against the current options list.
         *    Options depend on the current project version. If a version changed/deleted,
         *    old selections coming from URL params / stored filters might no longer exist
         *    in `options`. We must drop those stale selections to avoid UI bugs.
         *
         * 2) PRESERVING the option object (label, etc.) from `options`,
         *    but OVERRIDING `option.value` with the form value.
         *    Reason: `isExclude` is dynamic (user-controlled) and lives in the form state,
         *    while the fetched `options` always have `isExclude: false`.
         */
        const handleValue = (formValue?: Value[]): ISelectOption<Value>[] => {
            const result: ISelectOption<Value>[] = [];
            if (!formValue?.length) return result;

            options.forEach((listOptionElement: ISelectOption<Value>) => {
                const currentFormValueElement = formValue.find(
                    (formValueElement) => formValueElement?.name === listOptionElement.value.name,
                );

                if (currentFormValueElement) {
                    result.push({
                        ...listOptionElement,
                        value: {
                            ...currentFormValueElement,
                        },
                    });
                }
            });

            return result;
        };

        return isSelected ? (
            <div className={classes.selectWrapper}>
                <div data-testid={'tags-filter'}>
                    <Controller
                        render={({ field }) => (
                            <Select<ISelectOption<Value>, true>
                                {...field}
                                options={options}
                                isMulti
                                onChange={handleChange}
                                value={handleValue(field.value)}
                                placeholder={'Select Tags...'}
                                components={{
                                    Option: OptionsWithExclude,
                                }}
                                label={FilterLabels.TAG_CARD_BOARD}
                            />
                        )}
                        name={name}
                        control={methods?.control}
                    />
                </div>
            </div>
        ) : null;
    },
);
